import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  Alert,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Category as CategoryIcon,
  Straighten as StraightenIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import systemAPI from "../../utils/systemAPI";

/**
 * System Settings Component
 *
 * Allows the Developer Master to configure global system settings.
 * These settings affect the behavior of the entire application.
 */
const SystemSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tab, setTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editField, setEditField] = useState("");
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await systemAPI.getSystemSettings();
        setSettings(res.data);
      } catch (err) {
        setError("Failed to load system settings.");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleTabChange = (e, newValue) => setTab(newValue);

  const handleEditOpen = (field, value) => {
    setEditField(field);
    setEditValue(value);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const updated = { ...settings, [editField]: editValue };
      await systemAPI.updateSystemSettings(updated);
      setSettings(updated);
      setSuccess("Settings updated successfully.");
      setEditDialogOpen(false);
    } catch (err) {
      setError("Failed to update settings.");
    }
  };

  const handleChipDelete = async (type, value) => {
    try {
      const updated = { ...settings };
      updated[type] = updated[type].filter((v) => v !== value);
      await systemAPI.updateSystemSettings(updated);
      setSettings(updated);
      setSuccess(`${type === "categories" ? "Category" : "Unit"} removed.`);
    } catch (err) {
      setError("Failed to update settings.");
    }
  };

  const handleAddChip = async (type, value) => {
    if (!value) return;
    try {
      const updated = { ...settings };
      if (!updated[type].includes(value)) {
        updated[type].push(value);
        await systemAPI.updateSystemSettings(updated);
        setSettings(updated);
        setSuccess(`${type === "categories" ? "Category" : "Unit"} added.`);
      }
    } catch (err) {
      setError("Failed to update settings.");
    }
  };

  return (
    <Box
      sx={{
        width: "100%", // Fill available space, no overflow
        minHeight: "100vh",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        p: { xs: 1, sm: 2, md: 3, lg: 4 },
        m: 0,
      }}
    >
      <Typography variant="h4" gutterBottom fontWeight={700}>
        System Settings
      </Typography>
      <Typography variant="subtitle1" gutterBottom color="text.secondary">
        Configure global system settings, categories, and units
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}
      <Paper sx={{ mb: 3, width: "100%", boxSizing: "border-box" }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="General" icon={<SettingsIcon />} iconPosition="start" />
          <Tab
            label="Categories"
            icon={<CategoryIcon />}
            iconPosition="start"
          />
          <Tab label="Units" icon={<StraightenIcon />} iconPosition="start" />
        </Tabs>
        <Divider />
        <Box
          sx={{
            p: { xs: 1, sm: 2, md: 3 },
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Departmental Settings Notice and Link */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Departmental Categories & Units:</strong> To manage
            categories and units for each department separately, please use the{" "}
            <Button
              color="primary"
              variant="outlined"
              size="small"
              href="/developer-master/department-settings"
              sx={{ ml: 1 }}
            >
              Department Settings
            </Button>{" "}
            page. Changes here only affect global defaults.
          </Alert>
          {loading ? (
            <CircularProgress />
          ) : !settings ? (
            <Alert severity="warning">System settings not available.</Alert>
          ) : tab === 0 ? (
            <Grid
              container
              spacing={2}
              sx={{ width: "100%", boxSizing: "border-box" }}
            >
              <Grid item xs={12} md={6}>
                <Card sx={{ width: "100%" }}>
                  <CardContent>
                    <Typography variant="h6">
                      Default Minimum Threshold
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      The minimum quantity before an inventory item is flagged
                      as low stock.
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() =>
                        handleEditOpen(
                          "defaultMinThreshold",
                          settings?.defaultMinThreshold ?? 0
                        )
                      }
                    >
                      Edit
                    </Button>
                    <Typography variant="h5" sx={{ mt: 2 }}>
                      {settings?.defaultMinThreshold ?? 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {/* Add more general settings here as needed */}
            </Grid>
          ) : tab === 1 ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Inventory Categories
              </Typography>
              <Autocomplete
                freeSolo
                options={[]}
                onChange={(_, value) => handleAddChip("categories", value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add Category"
                    variant="outlined"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleAddChip("categories", e.target.value);
                        e.target.value = "";
                      }
                    }}
                  />
                )}
              />
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {settings?.categories?.map((cat) => (
                  <Chip
                    key={cat}
                    label={cat}
                    color="primary"
                    onDelete={() => handleChipDelete("categories", cat)}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Inventory Units
              </Typography>
              <Autocomplete
                freeSolo
                options={[]}
                onChange={(_, value) => handleAddChip("units", value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add Unit"
                    variant="outlined"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleAddChip("units", e.target.value);
                        e.target.value = "";
                      }
                    }}
                  />
                )}
              />
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {settings?.units?.map((unit) => (
                  <Chip
                    key={unit}
                    label={unit}
                    color="secondary"
                    onDelete={() => handleChipDelete("units", unit)}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Setting</DialogTitle>
        <DialogContent>
          <TextField
            label={editField}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemSettings;
